import React, { useState, useEffect, useRef } from "react";

import FeaturedCard from "./FeaturedCard";
import ViewBusiness from "./ViewBusiness";
import "./ViewAllService.css";

const ViewAllService = ({ 
  onBack, 
  onSignIn, 
  onJoinNow, 
  onAbout,
  fetchSpecializations,
  fetchServicesByCategory,
  searchResults,
  searchLoading,
  businessData,
  services,
  loading,
  activeSpec,
  setActiveSpec
}) => {
  const [search, setSearch] = useState("");
  const [selectedSpec, setSelectedSpec] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeCategories, setActiveCategories] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  // `fetchSpecializations` is a prop, so it's a new function reference
  // on every parent render unless the parent wraps it in useCallback.
  // Adding it directly to the effect below's dependency array would
  // satisfy the lint rule but re-run the initial fetch on every
  // re-render (not just on mount). Keeping the latest function in a
  // ref lets the effect call it without depending on a value that
  // changes every render — this is the standard fix for
  // "load-once-on-mount" effects that call a prop function.
  const fetchSpecializationsRef = useRef(fetchSpecializations);
  useEffect(() => {
    fetchSpecializationsRef.current = fetchSpecializations;
  }, [fetchSpecializations]);

  // Load initial data when component mounts
  useEffect(() => {
    fetchSpecializationsRef.current?.("");
  }, []);

  // Update categories when specialization changes
  useEffect(() => {
    if (selectedSpec && searchResults.categories) {
      const specId = searchResults.specializations.find(spec => spec.name === selectedSpec)?.id;
      if (specId && searchResults.categories[specId]) {
        setActiveCategories(searchResults.categories[specId]);
      } else {
        setActiveCategories([]);
      }
    } else {
      setActiveCategories([]);
    }
    setSelectedCategory(null); // Reset category when specialization changes
  }, [selectedSpec, searchResults]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (fetchSpecializations) {
      fetchSpecializations(search.trim());
    }
  };

  // Handle specialization selection
  const handleSpecializationSelect = (specName) => {
    setSelectedSpec(specName);
    const spec = searchResults.specializations.find(s => s.name === specName);
    if (spec && setActiveSpec && fetchServicesByCategory) {
      setActiveSpec(spec.id);
      // Call API with specialization ID
      fetchServicesByCategory(spec.id);
    }
  };

  // Handle category selection
  const handleCategorySelect = (categoryName, category) => {
    setSelectedCategory(categoryName);
    if (category && fetchServicesByCategory) {
      // Call API with both primaryId (specialization) and categoryId (subcategory)
      const currentSpecId = selectedSpec 
        ? searchResults.specializations.find(s => s.name === selectedSpec)?.id 
        : null;
      
      // Call API with category ID
      fetchServicesByCategory(currentSpecId, category.subCategoryId);
    }
  };

  // Handle view business
  const handleViewBusiness = (business) => {
    setSelectedBusiness(business);
  };

  // Handle chat with business
  const handleChat = (business) => {
    console.log("Starting chat with:", business.serviceName || business.userName);
    alert(`Starting chat with ${business.serviceName || business.userName}`);
  };

  // Helper to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>?/gm, "");
  };

  // Filter services based on search only (since API filtering handles spec/category)
  const getFilteredData = () => {
    let filteredBusinessData = [];
    let filteredServices = [];

    // Filter business data (only by search, since API handles spec/category filtering)
    if (businessData && businessData.length > 0) {
      filteredBusinessData = businessData.filter((item) => {
        const matchSearch = search === "" ||
          (item.serviceName || "").toLowerCase().includes(search.toLowerCase()) ||
          (item.userName || "").toLowerCase().includes(search.toLowerCase()) ||
          (item.serviceCategory || "").toLowerCase().includes(search.toLowerCase()) ||
          (item.location || "").toLowerCase().includes(search.toLowerCase()) ||
          stripHtml(item.description || item.serviceDetails || "").toLowerCase().includes(search.toLowerCase());

        return matchSearch;
      });
    }

    // Filter services data (only by search, since API handles spec/category filtering)
    if (services && services.length > 0) {
      filteredServices = services.filter((item) => {
        const matchSearch = search === "" ||
          (item.serviceName || "").toLowerCase().includes(search.toLowerCase()) ||
          (item.userName || "").toLowerCase().includes(search.toLowerCase()) ||
          (item.serviceCategory || "").toLowerCase().includes(search.toLowerCase()) ||
          (item.location || "").toLowerCase().includes(search.toLowerCase()) ||
          stripHtml(item.description || item.serviceDetails || "").toLowerCase().includes(search.toLowerCase());

        return matchSearch;
      });
    }

    return { filteredBusinessData, filteredServices };
  };

  const { filteredBusinessData, filteredServices } = getFilteredData();
  const totalResults = filteredBusinessData.length + filteredServices.length;

  // Show ViewBusiness component if a business is selected
  if (selectedBusiness) {
    return (
      <ViewBusiness
        business={selectedBusiness}
        onBack={() => setSelectedBusiness(null)}
        onChat={handleChat}
      />
    );
  }

  return (
    <div className="all-services-page">
      {/* Heading */}
      <div className="all-services-hero">
        <h1>All <span className="highlight">Services</span></h1>
        <p>
          Discover all available services from our trusted network of women entrepreneurs and professionals
        </p>
      </div>

      {/* Search */}
      <div className="all-services-bar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            className="all-services-search"
            type="text"
            placeholder="Search services, locations, or keywords…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit" className="search-submit-btn">
            {searchLoading ? "Searching..." : "Search"}
          </button>
        </form>
        
        {/* Specializations Filter */}
        <div className="filter-section">
          {/* <h4 className="filter-title">Specializations</h4> */}
          <div className="filter-buttons">
            {searchResults.specializations && searchResults.specializations.map((spec) => (
              <button
                key={spec.id}
                className={`filter-btn${selectedSpec === spec.name ? " active" : ""}`}
                onClick={() => handleSpecializationSelect(spec.name)}
                disabled={loading}
              >
                {spec.name}
              </button>
            ))}
          </div>
        </div>

        {/* Categories Filter - Only show if a specialization is selected */}
        {selectedSpec && activeCategories.length > 0 && (
          <div className="filter-section">
            {/* <h4 className="filter-title">Categories</h4> */}
            <div className="filter-buttons">
              {activeCategories.map((category) => (
                <button
                  key={category.subCategoryId}
                  className={`filter-btn${selectedCategory === category.subCategoryName ? " active" : ""}`}
                  onClick={() => handleCategorySelect(category.subCategoryName, category)}
                  disabled={loading}
                >
                  {category.subCategoryName}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="loading-container">
          <div className="loading-text">Loading services...</div>
        </div>
      )}

      {/* Business Directory Section */}
      {!loading && filteredBusinessData.length > 0 && (
        <div className="services-section">
            {/* ({filteredBusinessData.length} */}
          {/* <h3 className="section-title">Business Directory )</h3> */}
          <div className="all-services-grid">
            {filteredBusinessData.map((business) => (
              <FeaturedCard
                key={`business-${business.directoryId}`}
                item={business}
                type="business"
                onViewBusiness={handleViewBusiness}
              />
            ))}
          </div>
        </div>
      )}

      {/* Services Section */}
      {!loading && filteredServices.length > 0 && (
        <div className="services-section">
            {/* ({filteredServices.length}) */}
          {/* <h3 className="section-title">Services </h3> */}
          <div className="all-services-grid">
            {filteredServices.map((service) => (
              <FeaturedCard
                key={`service-${service.id}`}
                item={service}
                type="service"
              />
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {!loading && totalResults === 0 && (
        <div className="no-results">
          {search || selectedSpec || selectedCategory 
            ? "No services found with current filters." 
            : "Select a specialization to view services."}
        </div>
      )}

      {/* Back Button */}
      <div className="back-button-container">
        <button className="back-btn" onClick={onBack}>
          ← Back to Home
        </button>
      </div>
    </div>
  );
};

export default ViewAllService;